import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  Users,
  ChalkboardTeacher,
  X as CloseIcon,
  ChalkboardSimple,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// ============== Class create/edit dialog ==============
const EMPTY_CLASS = { session_id: "", name: "", section: "", description: "" };

function ClassDialog({ open, onOpenChange, initial, sessions, defaultSessionId, onSubmit }) {
  const [form, setForm] = useState(EMPTY_CLASS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? { ...EMPTY_CLASS, ...initial }
          : { ...EMPTY_CLASS, session_id: defaultSessionId || "" },
      );
      setError("");
    }
  }, [open, initial, defaultSessionId]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(cleanPayload(form));
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  const isEdit = Boolean(initial?.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="class-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit class" : "Create class"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Rename the class or adjust its section."
              : "Classes are scoped to an academic session. Same name+section can exist across different sessions."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!isEdit && (
            <div>
              <Label>Session *</Label>
              <Select value={form.session_id} onValueChange={(v) => setForm((f) => ({ ...f, session_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="class-session-select">
                  <SelectValue placeholder="Select session" />
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`class-session-option-${s.id}`}>
                      {s.name}
                      {s.is_active ? "  •  Active" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Class name *</Label>
              <Input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="e.g. Class 5"
                className="mt-1.5"
                data-testid="class-name-input"
              />
            </div>
            <div>
              <Label>Section</Label>
              <Input
                value={form.section || ""}
                onChange={update("section")}
                placeholder="e.g. A"
                className="mt-1.5"
                data-testid="class-section-input"
              />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={form.description || ""}
              onChange={update("description")}
              className="mt-1.5"
              placeholder="Optional"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || (!isEdit && !form.session_id)}
              className="bg-blue-900 hover:bg-blue-800"
              data-testid="class-submit-btn"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create class"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============== Manage Teachers dialog ==============
function ManageTeachersDialog({ open, onOpenChange, klass, allTeachers, onUpdated }) {
  const [assigned, setAssigned] = useState([]);
  const [selectedToAdd, setSelectedToAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!klass) return;
    try {
      const { data } = await api.get(`/school/classes/${klass.id}/teachers`);
      setAssigned(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [klass]);

  useEffect(() => {
    if (open) {
      setError("");
      setSelectedToAdd("");
      refresh();
    }
  }, [open, refresh]);

  const assignedIds = useMemo(() => new Set(assigned.map((t) => t.id)), [assigned]);
  const availableToAdd = useMemo(
    () => allTeachers.filter((t) => !assignedIds.has(t.id) && t.is_active),
    [allTeachers, assignedIds],
  );

  const add = async () => {
    if (!selectedToAdd) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/school/classes/${klass.id}/teachers`, { teacher_id: selectedToAdd });
      toast.success("Teacher assigned");
      setSelectedToAdd("");
      await refresh();
      onUpdated?.();
    } catch (e) {
      setError(formatApiError(e));
    }
    setBusy(false);
  };

  const remove = async (teacherId) => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/school/classes/${klass.id}/teachers/${teacherId}`);
      toast.success("Teacher unassigned");
      await refresh();
      onUpdated?.();
    } catch (e) {
      setError(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manage-teachers-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage teachers</DialogTitle>
          <DialogDescription>
            Assign teachers to <strong>{klass?.name}{klass?.section ? ` - ${klass.section}` : ""}</strong>. Teachers
            see only students from classes they are assigned to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Currently assigned</Label>
            {assigned.length === 0 ? (
              <div className="text-sm text-slate-500 px-3 py-3 bg-slate-50 rounded-md border border-slate-200">
                No teachers assigned yet.
              </div>
            ) : (
              <div className="space-y-2" data-testid="assigned-teachers-list">
                {assigned.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-md border border-slate-200"
                    data-testid={`assigned-teacher-${t.id}`}
                  >
                    <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center text-xs font-semibold text-slate-700 border border-slate-200">
                      {(t.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
                      <div className="text-xs text-slate-500 truncate">{t.email}</div>
                    </div>
                    <button
                      onClick={() => remove(t.id)}
                      disabled={busy}
                      data-testid={`unassign-teacher-${t.id}`}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Unassign"
                    >
                      <CloseIcon size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="mb-2 block">Add a teacher</Label>
            <div className="flex gap-2">
              <Select value={selectedToAdd} onValueChange={setSelectedToAdd}>
                <SelectTrigger className="flex-1" data-testid="add-teacher-select">
                  <SelectValue placeholder={availableToAdd.length ? "Choose teacher…" : "All teachers already assigned"} />
                </SelectTrigger>
                <SelectContent>
                  {availableToAdd.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`add-teacher-option-${t.id}`}>
                      {t.name} <span className="text-slate-400">— {t.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={add}
                disabled={!selectedToAdd || busy}
                data-testid="assign-teacher-btn"
                className="bg-blue-900 hover:bg-blue-800"
              >
                Assign
              </Button>
            </div>
            {availableToAdd.length === 0 && allTeachers.length === 0 && (
              <div className="text-xs text-slate-500 mt-2">
                No active teachers. Add one from the <strong>Teachers</strong> page first.
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Main page ==============
export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filterSession, setFilterSession] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [classDialog, setClassDialog] = useState({ open: false, initial: null });
  const [teachersDialog, setTeachersDialog] = useState({ open: false, klass: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterSession && filterSession !== "__all__" ? `?session_id=${filterSession}` : "";
      const [c, s, t] = await Promise.all([
        api.get(`/school/classes${params}`),
        api.get("/school/sessions"),
        api.get("/school/teachers?limit=200"),
      ]);
      setClasses(c.data);
      setSessions(s.data);
      setTeachers(t.data.items);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filterSession]);

  useEffect(() => {
    load();
  }, [load]);

  const activeSession = sessions.find((s) => s.is_active);
  const defaultSessionId = activeSession?.id || sessions[0]?.id || "";

  const create = async (payload) => {
    const { data } = await api.post("/school/classes", payload);
    setClasses((c) => [data, ...c]);
    toast.success(`Created ${data.name}${data.section ? ` - ${data.section}` : ""}`);
  };

  const edit = async (payload) => {
    const id = classDialog.initial.id;
    const { data } = await api.patch(`/school/classes/${id}`, payload);
    setClasses((c) => c.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
    toast.success("Class updated");
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/school/classes/${confirmDelete.id}`);
      setClasses((c) => c.filter((x) => x.id !== confirmDelete.id));
      toast.success("Class deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setConfirmDelete(null);
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
            Academics
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Classes <span className="text-slate-400 font-normal">({classes.length})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Create classes per academic session and assign the teachers responsible for them.
          </p>
        </div>
        <Button
          onClick={() => setClassDialog({ open: true, initial: null })}
          data-testid="add-class-btn"
          disabled={sessions.length === 0}
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> Add class
        </Button>
      </div>

      {sessions.length === 0 && (
        <div className="p-4 border border-amber-200 bg-amber-50 text-amber-800 rounded-md text-sm" data-testid="no-sessions-warning">
          You need at least one academic session before creating classes. Go to{" "}
          <strong>Sessions</strong> and add one.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
          {error}
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <Label className="text-xs text-slate-500 uppercase tracking-wider mr-1">Session</Label>
          <Select value={filterSession} onValueChange={setFilterSession}>
            <SelectTrigger className="w-56 h-9" data-testid="filter-session-select">
              <SelectValue placeholder="All sessions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sessions</SelectItem>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.is_active ? "  •  Active" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : classes.length === 0 ? (
          <div className="p-12 text-center" data-testid="classes-empty">
            <ChalkboardSimple size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">
              No classes yet for this filter. Click <strong>Add class</strong> to create one.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="classes-table">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left font-semibold px-4 py-3">Class</th>
                  <th className="text-left font-semibold px-4 py-3">Session</th>
                  <th className="text-right font-semibold px-4 py-3">Students</th>
                  <th className="text-right font-semibold px-4 py-3">Teachers</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {classes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`class-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {c.name}{c.section ? <span className="text-slate-400"> · </span> : null}
                        {c.section && <span className="text-slate-700">{c.section}</span>}
                      </div>
                      {c.description && (
                        <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{c.session_name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-slate-700 font-mono text-xs">
                        <Users size={12} /> {c.student_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-slate-700 font-mono text-xs">
                        <ChalkboardTeacher size={12} /> {c.teacher_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setTeachersDialog({ open: true, klass: c })}
                          data-testid={`manage-teachers-${c.id}`}
                          className="px-2.5 py-1 text-xs font-medium text-blue-900 hover:bg-blue-50 rounded"
                        >
                          Teachers
                        </button>
                        <button
                          onClick={() => setClassDialog({ open: true, initial: c })}
                          data-testid={`edit-class-${c.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          title="Edit"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(c)}
                          data-testid={`delete-class-${c.id}`}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ClassDialog
        open={classDialog.open}
        onOpenChange={(v) => setClassDialog({ open: v, initial: v ? classDialog.initial : null })}
        initial={classDialog.initial}
        sessions={sessions}
        defaultSessionId={defaultSessionId}
        onSubmit={classDialog.initial ? edit : create}
      />

      <ManageTeachersDialog
        open={teachersDialog.open}
        onOpenChange={(v) => setTeachersDialog({ open: v, klass: v ? teachersDialog.klass : null })}
        klass={teachersDialog.klass}
        allTeachers={teachers}
        onUpdated={load}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete class?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{confirmDelete?.name}{confirmDelete?.section ? ` - ${confirmDelete.section}` : ""}</strong> and unassign any teachers. Classes that already have students cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-class">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
